import { useEffect, useRef, useState, useCallback } from 'react';
import { Geolocation } from '@capacitor/geolocation';
import { isCapacitorNative } from '../utils/nativePlatform';
import {
  computeHeading,
  distanceMeters,
  emaLatLng,
  lerpHeading,
  shouldRejectJump,
  type LatLng,
} from '../utils/locationSmoothing';

export interface SmoothGpsState {
  position: LatLng;
  heading: number;
  speedMps: number;
  isMoving: boolean;
  accuracyM: number | null;
  updatedAt: number;
}

export type GpsError = 'denied' | 'unavailable' | 'timeout' | null;

const HEADING_HOLD_SPEED_MPS = 0.8;
const HEADING_HOLD_MOVE_M = 3;
const FIREBASE_REPORT_MS = 2500;

async function ensureNativeGpsPermission(): Promise<boolean> {
  try {
    const status = await Geolocation.checkPermissions();
    if (status.location === 'granted' || status.coarseLocation === 'granted') {
      return true;
    }
    const req = await Geolocation.requestPermissions();
    return req.location === 'granted' || req.coarseLocation === 'granted';
  } catch (e) {
    console.warn('[GPS] Capacitor permission error:', e);
    return false;
  }
}

function mapGeoError(code?: number): GpsError {
  if (code === 1) return 'denied';
  if (code === 2) return 'unavailable';
  if (code === 3) return 'timeout';
  return 'unavailable';
}

/**
 * Live phone GPS for rider navigation.
 * Accuracy-weighted EMA + heading hold when stationary / poor GPS.
 */
export function useSmoothGps(enabled: boolean, onUpdate?: (pos: LatLng) => void) {
  const [state, setState] = useState<SmoothGpsState | null>(null);
  const [error, setError] = useState<GpsError>(null);
  const [retryToken, setRetryToken] = useState(0);
  const filteredPosRef = useRef<LatLng | null>(null);
  const rawPosRef = useRef<LatLng | null>(null);
  const lastUpdatedAtRef = useRef(0);
  const lastAccuracyRef = useRef<number | null>(null);
  const headingRef = useRef(0);
  const speedRef = useRef(0);
  const lastReportRef = useRef(0);
  const watchRef = useRef<string | null>(null);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const retry = useCallback(() => {
    setError(null);
    setRetryToken((n) => n + 1);
  }, []);

  const applyPosition = useCallback(
    (lat: number, lng: number, speed?: number | null, heading?: number | null, accuracy?: number | null) => {
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      if (Math.abs(lat) < 0.01 && Math.abs(lng) < 0.01) return;

      const raw = { lat, lng };
      const prevFiltered = filteredPosRef.current;
      const prevRaw = rawPosRef.current;
      const now = Date.now();
      const elapsedMs = lastUpdatedAtRef.current ? now - lastUpdatedAtRef.current : 1000;
      const movedFromRaw = prevRaw ? distanceMeters(prevRaw, raw) : 0;
      const movedFromFiltered = prevFiltered ? distanceMeters(prevFiltered, raw) : 0;

      if (
        shouldRejectJump(prevFiltered || prevRaw, raw, {
          movedM: Math.max(movedFromRaw, movedFromFiltered),
          elapsedMs,
          accuracyM: accuracy,
          prevAccuracyM: lastAccuracyRef.current,
        })
      ) {
        console.warn('[GPS] ignored jump', Math.round(Math.max(movedFromRaw, movedFromFiltered)), 'm');
        return;
      }

      // Accuracy-weighted EMA (first fix = raw)
      const filtered = prevFiltered
        ? emaLatLng(prevFiltered, raw, accuracy, accuracy != null && accuracy > 50 ? 0.18 : 0.32)
        : raw;

      const moved = prevFiltered ? distanceMeters(prevFiltered, filtered) : 0;
      const reportedSpeed =
        speed != null && Number.isFinite(speed) && speed >= 0 ? speed : null;
      const derivedSpeed =
        elapsedMs > 0 ? Math.min(40, (moved / Math.max(0.05, elapsedMs / 1000))) : 0;
      const speedMps = reportedSpeed != null && reportedSpeed > 0.3 ? reportedSpeed : derivedSpeed;

      const isMoving = speedMps > HEADING_HOLD_SPEED_MPS || moved > HEADING_HOLD_MOVE_M;

      // Heading: hold when nearly stopped; else device compass > movement bearing
      if (isMoving) {
        if (heading != null && Number.isFinite(heading) && heading >= 0) {
          headingRef.current = lerpHeading(headingRef.current, heading, 0.45);
        } else if (prevFiltered && moved >= HEADING_HOLD_MOVE_M) {
          const bearing = computeHeading(prevFiltered, filtered);
          headingRef.current = lerpHeading(headingRef.current, bearing, 0.35);
        }
      }
      // else: hold headingRef — stops spin at pickup / red lights

      filteredPosRef.current = filtered;
      rawPosRef.current = raw;
      lastAccuracyRef.current = accuracy ?? lastAccuracyRef.current;
      speedRef.current = speedMps;
      setError(null);

      const snap: SmoothGpsState = {
        position: filtered,
        heading: headingRef.current,
        speedMps,
        isMoving,
        accuracyM: accuracy ?? null,
        updatedAt: now,
      };
      lastUpdatedAtRef.current = now;
      setState(snap);

      if (now - lastReportRef.current >= FIREBASE_REPORT_MS) {
        lastReportRef.current = now;
        onUpdateRef.current?.(filtered);
      }
    },
    []
  );

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let staleTimer: number | null = null;

    const clearWatch = () => {
      if (watchRef.current == null) return;
      if (isCapacitorNative()) {
        void Geolocation.clearWatch({ id: watchRef.current });
      } else if (typeof navigator !== 'undefined' && navigator.geolocation) {
        navigator.geolocation.clearWatch(Number(watchRef.current));
      }
      watchRef.current = null;
    };

    const startWebWatch = (highAccuracy: boolean) => {
      if (!('geolocation' in navigator)) {
        setError('unavailable');
        return;
      }

      const onOk = (pos: GeolocationPosition) => {
        if (cancelled) return;
        applyPosition(
          pos.coords.latitude,
          pos.coords.longitude,
          pos.coords.speed,
          pos.coords.heading,
          pos.coords.accuracy
        );
      };
      const onErr = (err: GeolocationPositionError) => {
        console.warn('[GPS] web error:', err?.message || err);
        if (!cancelled) setError(mapGeoError(err?.code));
      };

      clearWatch();
      navigator.geolocation.getCurrentPosition(onOk, onErr, {
        enableHighAccuracy: highAccuracy,
        timeout: highAccuracy ? 15000 : 25000,
        maximumAge: 0,
      });

      watchRef.current = String(
        navigator.geolocation.watchPosition(onOk, onErr, {
          enableHighAccuracy: highAccuracy,
          maximumAge: 0,
          timeout: highAccuracy ? 15000 : 25000,
        })
      );
    };

    const start = async () => {
      clearWatch();
      filteredPosRef.current = null;
      rawPosRef.current = null;

      if (isCapacitorNative()) {
        const ok = await ensureNativeGpsPermission();
        if (!ok || cancelled) {
          setError('denied');
          return;
        }

        try {
          const pos = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 20000,
            maximumAge: 0,
          });
          if (!cancelled) {
            applyPosition(
              pos.coords.latitude,
              pos.coords.longitude,
              pos.coords.speed,
              pos.coords.heading,
              pos.coords.accuracy
            );
          }
        } catch (e) {
          console.warn('[GPS] native getCurrentPosition failed:', e);
          if (!cancelled) setError('timeout');
        }

        watchRef.current = await Geolocation.watchPosition(
          { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
          (pos, err) => {
            if (cancelled) return;
            if (err || !pos) {
              setError('unavailable');
              return;
            }
            applyPosition(
              pos.coords.latitude,
              pos.coords.longitude,
              pos.coords.speed,
              pos.coords.heading,
              pos.coords.accuracy
            );
          }
        );
      } else {
        startWebWatch(true);
        staleTimer = window.setInterval(() => {
          if (cancelled) return;
          const age = lastUpdatedAtRef.current
            ? Date.now() - lastUpdatedAtRef.current
            : 99999;
          startWebWatch(age > 20000 ? false : true);
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              if (cancelled) return;
              applyPosition(
                pos.coords.latitude,
                pos.coords.longitude,
                pos.coords.speed,
                pos.coords.heading,
                pos.coords.accuracy
              );
            },
            () => undefined,
            { enableHighAccuracy: false, maximumAge: 0, timeout: 12000 }
          );
        }, 12000);
      }
    };

    void start();

    return () => {
      cancelled = true;
      clearWatch();
      if (staleTimer != null) window.clearInterval(staleTimer);
    };
  }, [enabled, applyPosition, retryToken]);

  return {
    ...state,
    position: state?.position,
    error,
    retry,
    hasFix: Boolean(state?.position),
  };
}
